import React, { useMemo, useState } from "react";
import { getSuperAdminLoginLogs, SuperAdminLoginLog } from "../../services/superAdminApi";

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const SuperAdminLoginLogs: React.FC = () => {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const thirtyDaysAgo = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().slice(0, 10);
  }, []);

  const [rows, setRows] = useState<SuperAdminLoginLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);

  const load = async (nextPage = 1, nextSearch = search, nextPerPage = perPage) => {
    try {
      setLoading(true);
      setError("");
      const params: Record<string, any> = {
        start_date: startDate,
        end_date: endDate,
        page: nextPage,
        per_page: nextPerPage,
      };
      if (nextSearch.trim()) {
        params.search = nextSearch.trim();
      }

      const res = await getSuperAdminLoginLogs(params);
      setRows(res?.data ?? []);
      setPage(res?.current_page ?? nextPage);
      setLastPage(res?.last_page ?? 1);
      setTotal(res?.total ?? 0);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load login logs.");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load(1, "", perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-5 p-1">
      <div>
        <h1 className="text-2xl font-semibold text-[#402B69]">Login Logs</h1>
        <p className="text-sm text-gray-500">Company user login and logout events.</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Start Date</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">End Date</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" />
        </div>
        <button onClick={() => load(1, search)} className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-4 py-2 rounded-lg text-sm font-semibold">
          Apply Date Range
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b flex flex-col gap-2 md:flex-row md:items-center">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by user, email, company, IP..."
            className="w-full md:max-w-sm border rounded-lg px-3 py-2 text-sm"
          />
          <div className="flex gap-2">
            <button
              onClick={() => {
                const nextSearch = searchInput.trim();
                setSearch(nextSearch);
                load(1, nextSearch);
              }}
              className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-4 py-2 rounded-lg text-sm font-semibold"
            >
              Search
            </button>
            <button
              onClick={() => {
                setSearchInput("");
                setSearch("");
                load(1, "");
              }}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold"
            >
              Clear
            </button>
          </div>
          <div className="md:ml-auto">
            <label className="block text-xs text-gray-500 mb-1">Rows per page</label>
            <select
              value={perPage}
              onChange={(e) => {
                const nextPerPage = Number(e.target.value);
                setPerPage(nextPerPage);
                load(1, search, nextPerPage);
              }}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
        {loading ? (
          <div className="p-6 text-gray-600">Loading login logs...</div>
        ) : error ? (
          <div className="p-6 text-red-600">{error}</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">User</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Company</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Event</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">IP</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td className="px-4 py-6 text-gray-500" colSpan={5}>No login logs found.</td>
                    </tr>
                  ) : (
                    rows.map((log) => (
                      <tr key={log.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-[#402B69]">{log.user?.name || log.email_attempted || "-"}</div>
                          <div className="text-xs text-gray-500">{log.user?.email || "-"}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{log.company?.company_name || "-"}</td>
                        <td className="px-4 py-3 text-gray-700">{log.event || "-"}</td>
                        <td className="px-4 py-3 text-gray-700">{log.ip_address || "-"}</td>
                        <td className="px-4 py-3 text-gray-700">{formatDateTime(log.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="border-t px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-gray-500">Showing page {page} of {lastPage} ({total} total logs)</p>
              <div className="flex items-center gap-2">
                <button onClick={() => load(Math.max(1, page - 1), search)} disabled={page <= 1 || loading} className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50">Previous</button>
                <button onClick={() => load(Math.min(lastPage, page + 1), search)} disabled={page >= lastPage || loading} className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50">Next</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SuperAdminLoginLogs;

